/**
 * Типи для кастомних трансформерів відповідей
 */

/**
 * Результат трансформації відповіді колекції
 */
export interface ICollectionTransformResult<T = any> {
    /** Масив даних колекції */
    items: T[]

    /** Додаткові метадані (pagination, totals тощо) */
    meta?: Record<string, any>
}

/**
 * Функція-трансформер для обробки відповіді колекції
 * @param data - оригінальні дані з API
 * @returns трансформовані дані з масивом items та опціональними meta
 */
export type CollectionTransformer = (data: any) => ICollectionTransformResult | any[]

/**
 * Вбудовані трансформери для популярних API форматів
 */
export const CollectionTransformers = {
    /**
     * API Platform формат (hydra:member)
     * @example { "hydra:member": [...], "hydra:totalItems": 100 }
     */
    apiPlatform: (data: any): ICollectionTransformResult | any[] => {
        if (Array.isArray(data['hydra:member'])) {
            return {
                items: data['hydra:member'],
                meta: {
                    totalItems: data['hydra:totalItems'],
                    view: data['hydra:view'],
                },
            }
        }
        return data
    },

    /**
     * Простий формат (items)
     * @example { "items": [...], "total": 100, "page": 1 }
     */
    items: (data: any): ICollectionTransformResult | any[] => {
        if (Array.isArray(data.items)) {
            return {
                items: data.items,
                meta: {
                    total: data.total,
                    page: data.page,
                    pageSize: data.pageSize,
                    totalPages: data.totalPages,
                },
            }
        }
        return data
    },

    /**
     * Результати формат (results)
     * @example { "results": [...], "count": 100 }
     */
    results: (data: any): ICollectionTransformResult | any[] => {
        if (Array.isArray(data.results)) {
            return {
                items: data.results,
                meta: {
                    count: data.count,
                    next: data.next,
                    previous: data.previous,
                },
            }
        }
        return data
    },

    /**
     * Просто масив
     * @example [...]
     */
    array: (data: any): ICollectionTransformResult | any[] => {
        if (Array.isArray(data)) {
            return {
                items: data,
                meta: {},
            }
        }
        return data
    },

    /**
     * Не трансформувати (повернути як є)
     */
    none: (data: any): any => data,
}

/**
 * Тип для назв вбудованих трансформерів
 */
export type CollectionTransformerName = keyof typeof CollectionTransformers
